import { BankConnectionMapper } from '@core/mapper/bankConnectionMapper';
import { Bad } from '@core/model/framework/Result/BadResult';
import { Ok } from '@core/model/framework/Result/OkResult';
import {
  BankService,
  GetWebauthUrlServiceInput,
  GetWebauthUrlServiceOutput,
} from '@core/model/service/bankService';
import { GetBankConnectionsInput, GetBankConnectionsOutput } from '@core/usecase/getBankConnections';
import { GetBanksOutput } from '@core/usecase/getBanks';
import {
  SaveWebauthBankConnectionInput,
  SaveWebauthBankConnectionOutput,
} from '@core/usecase/saveWebauthBankConnection';
import { SERVICE_ID } from '@di/serviceId';
import { ConnectionFormItemDto } from '@modules/process/pages/login/services/dtos/loginDtos';
import { injectable, inject } from 'inversify';
import type { HttpClient } from '@core/model/framework/httpClient';
import { GetBankConnectionsDto } from './types/bankConnectionDto';
import { BankDto } from './types/bankDto';
import { GetWebauthUrlDto } from './types/getWebauthUrlDto';

@injectable()
export class DFBankService implements BankService {
  constructor(
    @inject(SERVICE_ID.openBankingHttpClient)
    private readonly httpClient: HttpClient,
    @inject(BankConnectionMapper)
    private readonly bankConnectionMapper: BankConnectionMapper
  ) {}

  async getWebauthUrl(input: GetWebauthUrlServiceInput): GetWebauthUrlServiceOutput {
    const { userToken, bankId, form, tagging } = input;

    const body: { form: ConnectionFormItemDto[] | undefined; tagging: string | undefined } = {
      tagging,
      form: form?.value,
    };

    const result = await this.httpClient.post<
      GetWebauthUrlDto,
      { form: ConnectionFormItemDto[] | undefined; tagging: string | undefined }
    >(`/auth/webauth-url?bankId=${bankId}`, body, {
      Authorization: `Bearer ${userToken}`,
    });

    if (result.success === false) {
      return Bad('GET_WEBAUTH_URL_FAILED');
    }

    return Ok(result.value.data.url);
  }

  async getBanks(): GetBanksOutput {
    const result = await this.httpClient.get<BankDto[]>('/banks');

    if (result.success === false) {
      return Bad('GET_BANKS_FAILED');
    }

    const oneyCustomBank = result.value.data.find((bank) => {
      return bank.uid === 'UID-CUSTOM-BANK';
    });

    if (oneyCustomBank != null) {
      oneyCustomBank.form[0]?.options?.forEach((option) => {
        const splitDescription = option.name.split('=>');
        option.name = splitDescription[0]?.trim() ?? '';

        if (splitDescription.length === 1) {
          option.description = `Pas d'informations complémentaires`;
        } else {
          option.description = splitDescription[1]?.trim() ?? '';
        }
      });
    }

    return Ok(result.value.data);
  }

  async saveWebauthBankConnection(input: SaveWebauthBankConnectionInput): SaveWebauthBankConnectionOutput {
    const { webauthPopupConnectionId, webauthPopupUserToken } = input;

    const result = await this.httpClient.post<object, { externalConnectionId: string }>(
      `/auth/webauth-connection`,
      {
        externalConnectionId: webauthPopupConnectionId,
      },
      {
        Authorization: `Bearer ${webauthPopupUserToken}`,
      }
    );

    if (result.success === true) {
      return Ok();
    } else {
      return Bad('SAVE_BANK_CONNECTION_FAILED');
    }
  }

  async getBankConnections(input: GetBankConnectionsInput): GetBankConnectionsOutput {
    const { userId, userToken } = input;
    const result = await this.httpClient.get<GetBankConnectionsDto>(`/users/${userId}/connections`, {
      Authorization: `Bearer ${userToken}`,
    });
    if (result.success === true) {
      const bankConnectionsDto = result.value.data;
      const bankConnections = bankConnectionsDto.map((bc) => this.bankConnectionMapper.toDomain(bc));
      return Ok(bankConnections);
    } else {
      return Bad('GET_BANK_CONNECTION_FAILED');
    }
  }
}
